import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../services/api';

interface CalOrder {
  id: string;
  orderNumber: string;
  dealer: string;
  sidemark: string;
  units: number;
  status: string;
  priority: string;
  currentStation: string;
  workOrderId: string | null;
  progress: number;
  scheduledDate: string | null;
  dueDate: string | null;
  sortOrder: number;
}
interface CalStats { total: number; rush: number; overdue: number; unscheduled: number; }

// Priority visuals (read-only) — mirrors the factory calendar
const PRIORITY: Record<string, { label: string; dot: string; bar: string; border: string }> = {
  rush:   { label: 'Rush',   dot: 'bg-red-500',    bar: 'bg-red-500',    border: '#DC2626' },
  high:   { label: 'High',   dot: 'bg-orange-500', bar: 'bg-orange-500', border: '#EA580C' },
  normal: { label: 'Normal', dot: 'bg-blue-500',   bar: 'bg-blue-500',   border: '#3B82F6' },
  low:    { label: 'Low',    dot: 'bg-gray-400',   bar: 'bg-gray-400',   border: '#9CA3AF' },
};
const prioRank: Record<string, number> = { rush: 0, high: 1, normal: 2, low: 3 };

const fmtDay = (d: Date) => d.toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' });
const isoDay = (d: Date) => { const off = d.getTimezoneOffset() * 60000; return new Date(d.getTime() - off).toISOString().split('T')[0]; };
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d; };
const statusColor = (s: string) =>
  s === 'in_production' ? 'bg-blue-500/20 text-blue-300 border-blue-500/40'
  : s === 'pending_release' ? 'bg-amber-500/20 text-amber-300 border-amber-500/40'
  : 'bg-gray-500/20 text-gray-300 border-gray-500/40';

// Compact read-only order card for month cells
function MiniCard({ o, onClick }: { o: CalOrder; onClick: () => void }) {
  const p = PRIORITY[o.priority] || PRIORITY.normal;
  const overdue = o.dueDate && new Date(o.dueDate) < new Date();
  return (
    <button
      onClick={onClick}
      className="w-full text-left bg-gray-900 rounded-lg mb-1.5 px-2 py-1.5 border border-gray-700 hover:border-gray-500"
      style={{ borderLeftWidth: 4, borderLeftColor: p.border }}
    >
      <div className="flex items-center justify-between gap-1">
        <span className="font-bold text-white text-sm truncate">{o.orderNumber}</span>
        <span className={`w-2.5 h-2.5 rounded-full shrink-0 ${p.dot}`} />
      </div>
      <div className="text-gray-400 text-xs truncate mt-0.5">{o.dealer} · {o.units}u</div>
      <div className="text-gray-500 text-[11px] truncate">{o.currentStation}</div>
      <div className="h-1.5 bg-gray-700 rounded-full overflow-hidden mt-1">
        <div className={`h-full ${p.bar}`} style={{ width: `${Math.min(100, o.progress)}%` }} />
      </div>
      <div className="flex justify-between mt-0.5 text-[11px]">
        <span className="text-gray-500">{o.progress}%</span>
        {o.dueDate && (
          <span className={overdue ? 'text-red-400 font-semibold' : 'text-gray-500'}>
            {overdue ? '! ' : ''}Due {new Date(o.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </span>
        )}
      </div>
    </button>
  );
}

export function ProductionCalendarPage() {
  const navigate = useNavigate();
  // Open an order's work-order detail page so the operator can pick their station.
  const openOrder = (o: CalOrder) => {
    if (o.workOrderId) navigate(`/work/${o.workOrderId}`);
  };
  const [orders, setOrders] = useState<CalOrder[]>([]);
  const [stats, setStats] = useState<CalStats>({ total: 0, rush: 0, overdue: 0, unscheduled: 0 });
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState<'day' | 'month'>('day');
  const [offset, setOffset] = useState(0);
  const [monthCursor, setMonthCursor] = useState(() => { const d = startOfToday(); d.setDate(1); return d; });
  const [prioFilter, setPrioFilter] = useState('all');

  useEffect(() => {
    (async () => {
      try {
        const [cal, st] = await Promise.all([api.getProductionCalendar(), api.getProductionCalendarStats()]);
        setOrders(cal.scheduled || []);
        setStats(st);
      } finally { setLoading(false); }
    })();
  }, []);

  const filtered = useMemo(
    () => (prioFilter === 'all' ? orders : orders.filter(o => o.priority === prioFilter)),
    [orders, prioFilter],
  );
  const byDate = useMemo(() => {
    const m: Record<string, CalOrder[]> = {};
    for (const o of filtered) { if (o.scheduledDate) (m[o.scheduledDate] ||= []).push(o); }
    for (const k in m) m[k].sort((a, b) => (prioRank[a.priority] ?? 2) - (prioRank[b.priority] ?? 2) || a.sortOrder - b.sortOrder);
    return m;
  }, [filtered]);

  const day = useMemo(() => { const d = startOfToday(); d.setDate(d.getDate() + offset); return d; }, [offset]);
  const dayIso = isoDay(day);
  const dayOrders = byDate[dayIso] || [];
  const dayLabel = offset === 0 ? 'Today' : offset === 1 ? 'Tomorrow' : offset === -1 ? 'Yesterday' : fmtDay(day);

  const goToDate = (d: Date) => { setOffset(Math.round((d.getTime() - startOfToday().getTime()) / 86400000)); setView('day'); };

  const monthGrid = useMemo(() => {
    const gridStart = new Date(monthCursor);
    gridStart.setDate(monthCursor.getDate() - monthCursor.getDay());
    return Array.from({ length: 42 }, (_, i) => { const d = new Date(gridStart); d.setDate(gridStart.getDate() + i); return d; });
  }, [monthCursor]);
  const todayIso = isoDay(startOfToday());

  return (
    <div className="p-4 w-full">
      <div className="flex items-start justify-between gap-3 mb-1">
        <h1 className="text-2xl font-bold text-white">Production Calendar</h1>
        <div className="flex bg-gray-800 rounded-lg p-1 shrink-0">
          {(['day', 'month'] as const).map(v => (
            <button key={v} onClick={() => setView(v)}
              className={`px-3 py-1.5 text-sm font-medium rounded-md capitalize ${view === v ? 'bg-yellow-500 text-black' : 'text-gray-300 hover:text-white'}`}>
              {v}
            </button>
          ))}
        </div>
      </div>
      <p className="text-gray-400 text-sm mb-4">Orders scheduled for production — what's on the floor each day.</p>

      {/* Stats bar (mirrors factory) */}
      <div className="flex gap-2 mb-4 flex-wrap">
        {[
          { label: 'Total', value: stats.total, c: 'text-blue-300' },
          { label: 'Rush', value: stats.rush, c: 'text-red-300' },
          { label: 'Overdue', value: stats.overdue, c: 'text-orange-300' },
          { label: 'Unscheduled', value: stats.unscheduled, c: 'text-purple-300' },
        ].map(s => (
          <div key={s.label} className="bg-gray-800 border border-gray-700 rounded-lg px-3 py-1.5 flex items-center gap-2">
            <span className={`text-lg font-bold ${s.c}`}>{s.value}</span>
            <span className="text-[11px] text-gray-400">{s.label}</span>
          </div>
        ))}
      </div>

      {loading ? (
        <div className="flex justify-center py-16"><div className="animate-spin h-8 w-8 border-b-2 border-yellow-400 rounded-full" /></div>
      ) : view === 'day' ? (
        <>
          <div className="flex items-center justify-between bg-gray-800 rounded-xl p-3 mb-4">
            <button onClick={() => setOffset(o => o - 1)} className="px-3 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600">‹ Prev</button>
            <div className="text-center">
              <div className="text-lg font-bold text-white">{dayLabel}</div>
              <div className="text-xs text-gray-400">{fmtDay(day)}</div>
            </div>
            <button onClick={() => setOffset(o => o + 1)} className="px-3 py-2 rounded-lg bg-gray-700 text-white hover:bg-gray-600">Next ›</button>
          </div>
          {offset !== 0 && (
            <button onClick={() => setOffset(0)} className="w-full mb-4 py-2 rounded-lg bg-yellow-500/20 text-yellow-400 text-sm font-medium hover:bg-yellow-500/30">Jump to Today</button>
          )}
          {dayOrders.length === 0 ? (
            <div className="text-center py-16 text-gray-500 bg-gray-800/50 rounded-xl">No orders scheduled for {dayLabel.toLowerCase()}.</div>
          ) : (
            <div className="space-y-3">
              <div className="text-sm text-gray-400">{dayOrders.length} order{dayOrders.length !== 1 ? 's' : ''} · {dayOrders.reduce((s, o) => s + o.units, 0)} units</div>
              {dayOrders.map(o => {
                const p = PRIORITY[o.priority] || PRIORITY.normal;
                const overdue = o.dueDate && new Date(o.dueDate) < new Date();
                return (
                  <div
                    key={o.id}
                    onClick={() => openOrder(o)}
                    role={o.workOrderId ? 'button' : undefined}
                    className={`bg-gray-800 rounded-xl p-4 border border-gray-700 transition-colors ${o.workOrderId ? 'cursor-pointer hover:border-yellow-500 active:bg-gray-750' : ''}`}
                    style={{ borderLeftWidth: 4, borderLeftColor: p.border }}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div>
                        <div className="font-bold text-white text-lg">{o.orderNumber}</div>
                        <div className="text-gray-300">{o.dealer}</div>
                        {o.sidemark && <div className="text-gray-500 text-sm">{o.sidemark}</div>}
                      </div>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`px-2 py-1 text-xs font-medium rounded-full border ${statusColor(o.status)}`}>{o.status.replace(/_/g, ' ')}</span>
                        <span className="flex items-center gap-1 text-[11px] text-gray-400"><span className={`w-2 h-2 rounded-full ${p.dot}`} />{p.label}</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between mt-3 text-sm">
                      <span className="text-gray-400">{o.units} unit{o.units !== 1 ? 's' : ''}</span>
                      <span className="text-gray-300">Station: <strong className="text-white">{o.currentStation}</strong></span>
                    </div>
                    {o.workOrderId && (
                      <div className="mt-2 text-right text-xs font-medium text-yellow-400">Tap to open work order ›</div>
                    )}
                    <div className="mt-2">
                      <div className="h-2 bg-gray-700 rounded-full overflow-hidden"><div className={`h-full ${p.bar}`} style={{ width: `${Math.min(100, o.progress)}%` }} /></div>
                      <div className="flex justify-between mt-1 text-[11px]">
                        <span className="text-gray-500">{o.progress}% complete</span>
                        {o.dueDate && <span className={overdue ? 'text-red-400 font-semibold' : 'text-gray-500'}>{overdue ? '! ' : ''}Due {new Date(o.dueDate).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</span>}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      ) : (
        <>
          {/* Month toolbar */}
          <div className="flex items-center justify-between gap-2 mb-3 flex-wrap">
            <div className="flex items-center gap-2">
              <button onClick={() => setMonthCursor(m => { const d = new Date(m); d.setMonth(d.getMonth() - 1); return d; })} className="px-2.5 py-1.5 rounded-md bg-gray-700 text-white hover:bg-gray-600">‹</button>
              <button onClick={() => setMonthCursor(() => { const d = startOfToday(); d.setDate(1); return d; })} className="px-3 py-1.5 rounded-md bg-yellow-500 text-black text-xs font-semibold">Today</button>
              <button onClick={() => setMonthCursor(m => { const d = new Date(m); d.setMonth(d.getMonth() + 1); return d; })} className="px-2.5 py-1.5 rounded-md bg-gray-700 text-white hover:bg-gray-600">›</button>
              <h2 className="text-lg font-bold text-white ml-1">{monthCursor.toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}</h2>
            </div>
            <select value={prioFilter} onChange={e => setPrioFilter(e.target.value)} className="bg-gray-800 border border-gray-700 text-gray-200 rounded-md px-2 py-1.5 text-xs">
              <option value="all">All Priorities</option>
              <option value="rush">Rush</option><option value="high">High</option><option value="normal">Normal</option><option value="low">Low</option>
            </select>
          </div>

          {/* Weekday headers */}
          <div className="grid grid-cols-7 gap-1.5 mb-1.5 text-center text-xs font-bold text-gray-500 uppercase">
            {['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map(d => <div key={d} className="py-1">{d}</div>)}
          </div>
          {/* Month grid with order cards */}
          <div className="grid grid-cols-7 gap-1.5">
            {monthGrid.map((d, i) => {
              const iso = isoDay(d);
              const inMonth = d.getMonth() === monthCursor.getMonth();
              const list = byDate[iso] || [];
              const isToday = iso === todayIso;
              return (
                <div key={i} className={`min-h-[170px] rounded-lg p-1.5 border overflow-hidden ${inMonth ? 'bg-gray-800 border-gray-700' : 'bg-gray-900/40 border-gray-800'}`}>
                  <div className="flex items-center justify-between mb-1">
                    {isToday ? (
                      <span className="bg-yellow-500 text-black rounded-full w-7 h-7 inline-flex items-center justify-center text-sm font-bold">{d.getDate()}</span>
                    ) : (
                      <span className={`text-sm font-semibold ${inMonth ? 'text-gray-200' : 'text-gray-600'}`}>{d.getDate()}</span>
                    )}
                    {list.length > 0 && <span className="text-[11px] text-gray-500">{list.length}</span>}
                  </div>
                  <div className="max-h-[260px] overflow-y-auto">
                    {list.map(o => <MiniCard key={o.id} o={o} onClick={() => (o.workOrderId ? openOrder(o) : goToDate(d))} />)}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Priority legend */}
          <div className="flex items-center gap-4 mt-3 flex-wrap text-[11px] text-gray-400">
            <span className="font-semibold text-gray-300">Priority:</span>
            {Object.entries(PRIORITY).map(([k, v]) => (
              <span key={k} className="flex items-center gap-1"><span className={`w-2.5 h-2.5 rounded-full ${v.dot}`} />{v.label}</span>
            ))}
            <span className="text-gray-500">· Tap a card to open its day.</span>
          </div>
        </>
      )}
    </div>
  );
}
